import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Package,
  AlertTriangle,
  ArrowRightLeft,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { saveToLocalDB, getFromLocalDB, addToSyncQueue } from '@/lib/offlineStorage';

const API = process.env.REACT_APP_BACKEND_URL;

// Unit conversion types
const UNIT_TYPES = {
  SACK_TO_KG: { wholesale: 'sack', retail: 'kg', label: 'Sack → Kilogram' },
  SACK_TO_PACK: { wholesale: 'sack', retail: 'pack', label: 'Sack → Pack' },
  BOX_TO_PIECE: { wholesale: 'box', retail: 'piece', label: 'Box → Piece' },
  CASE_TO_BOTTLE: { wholesale: 'case', retail: 'bottle', label: 'Case → Bottle' },
  NO_CONVERSION: { wholesale: 'unit', retail: 'unit', label: 'No Conversion' }
};

const CATEGORIES = [
  'Feeds',
  'Fertilizers',
  'Seeds',
  'Pesticides',
  'Veterinary',
  'Tools & Equipment',
  'Others'
];

const NASProducts = () => {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    sku: '',
    description: '',
    supplierName: '',
    unitType: 'SACK_TO_KG',
    conversionRate: 50,
    purchasePricePerSack: 0,
    purchasePricePerKg: 0,
    sellingPricePerSack: 0,
    sellingPricePerKg: 0,
    freightPerSack: 0,
    freightPerUnit: 0,
    handlingPerSack: 0,
    handlingPerUnit: 0,
    srIncentivePerSack: 0,
    srIncentivePerUnit: 0,
    lowStockThreshold: 10,
    // Sunjin-specific deductions
    dealerDiscountPerSack: 0,
    cashDiscountPerSack: 0,
    dealerRebatePerSack: 0,
    delivery: 0,
    marketingFund: 0,
    srMotorPlan: 0,
    manpowerIncentives: 0
  });
  
  // Check if supplier is Sunjin
  const isSunjinSupplier = formData.supplierName?.toLowerCase().includes('sunjin');
  
  useEffect(() => {
    loadProducts();
  }, []);
  
  const loadProducts = async () => {
    setIsLoading(true);
    try {
      // Try to fetch from API first
      if (navigator.onLine) {
        try {
          const response = await axios.get(`${API}/api/nas/products`);
          const apiProducts = response.data;
          setProducts(apiProducts);
          // Cache in IndexedDB for offline access
          await saveToLocalDB('nas_products', apiProducts);
        } catch (apiError) {
          console.error('API error, falling back to local:', apiError);
          // If API fails, try local storage
          const localProducts = await getFromLocalDB('nas_products');
          if (localProducts && localProducts.length > 0) {
            setProducts(localProducts);
          }
        }
      } else {
        // Offline - load from IndexedDB
        const localProducts = await getFromLocalDB('nas_products');
        if (localProducts && localProducts.length > 0) {
          setProducts(localProducts);
          toast.info('Offline mode - showing cached products');
        }
      }
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Failed to load products');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSaveProduct = async () => {
    if (!formData.name || !formData.category) {
      toast.error('Please fill in required fields (Name and Category)');
      return;
    }
    
    setIsSaving(true);
    
    try {
      if (navigator.onLine) {
        // Online - save to API
        if (editingProduct) {
          // Update existing product
          const response = await axios.put(`${API}/api/nas/products/${editingProduct.id}`, formData);
          const updatedProduct = response.data;
          const updatedProducts = products.map(p => p.id === editingProduct.id ? updatedProduct : p);
          setProducts(updatedProducts);
          await saveToLocalDB('nas_products', updatedProducts);
          toast.success('Product updated successfully');
        } else {
          // Create new product
          const response = await axios.post(`${API}/api/nas/products`, formData);
          const newProduct = response.data;
          const updatedProducts = [...products, newProduct];
          setProducts(updatedProducts);
          await saveToLocalDB('nas_products', updatedProducts);
          toast.success('Product added successfully');
        }
      } else {
        // Offline - save locally and queue for sync
        const productData = {
          ...formData,
          id: editingProduct?.id || `local_${Date.now()}`,
          currentStock: editingProduct?.currentStock || { wholesale: 0, retail: 0 },
          shopStock: editingProduct?.shopStock || { wholesale: 0, retail: 0 },
          bodegaStock: editingProduct?.bodegaStock || 0,
          createdAt: editingProduct?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          _pendingSync: true
        };
        
        let updatedProducts;
        if (editingProduct) {
          updatedProducts = products.map(p => p.id === editingProduct.id ? productData : p);
        } else {
          updatedProducts = [...products, productData];
        }
        
        setProducts(updatedProducts);
        await saveToLocalDB('nas_products', updatedProducts);
        await addToSyncQueue({
          type: editingProduct ? 'UPDATE_PRODUCT' : 'CREATE_PRODUCT',
          data: productData,
          timestamp: new Date().toISOString()
        });
        
        toast.success(`Product ${editingProduct ? 'updated' : 'added'} (will sync when online)`);
      }
      
      resetForm();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
    
    try {
      if (navigator.onLine) {
        await axios.delete(`${API}/api/nas/products/${product.id}`);
        const updatedProducts = products.filter(p => p.id !== product.id);
        setProducts(updatedProducts);
        await saveToLocalDB('nas_products', updatedProducts);
        toast.success('Product deleted successfully');
      } else {
        const updatedProducts = products.filter(p => p.id !== product.id);
        setProducts(updatedProducts);
        await saveToLocalDB('nas_products', updatedProducts);
        await addToSyncQueue({
          type: 'DELETE_PRODUCT',
          data: { id: product.id },
          timestamp: new Date().toISOString()
        });
        toast.success('Product deleted (will sync when online)');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    }
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      sku: '',
      description: '',
      supplierName: '',
      unitType: 'SACK_TO_KG',
      conversionRate: 50,
      purchasePricePerSack: 0,
      purchasePricePerKg: 0,
      sellingPricePerSack: 0,
      sellingPricePerKg: 0,
      freightPerSack: 0,
      freightPerUnit: 0,
      handlingPerSack: 0,
      handlingPerUnit: 0,
      srIncentivePerSack: 0,
      srIncentivePerUnit: 0,
      lowStockThreshold: 10,
      // Sunjin-specific deductions
      dealerDiscountPerSack: 0,
      cashDiscountPerSack: 0,
      dealerRebatePerSack: 0,
      delivery: 0,
      marketingFund: 0,
      srMotorPlan: 0,
      manpowerIncentives: 0
    });
    setEditingProduct(null);
    setIsAddModalOpen(false);
  };
  
  const openEditModal = (product) => {
    setFormData({
      name: product.name,
      category: product.category,
      sku: product.sku || '',
      description: product.description || '',
      supplierName: product.supplierName || '',
      unitType: product.unitType,
      conversionRate: product.conversionRate,
      purchasePricePerSack: product.purchasePricePerSack || product.wholesalePrice || 0,
      purchasePricePerKg: product.purchasePricePerKg || 0,
      sellingPricePerSack: product.sellingPricePerSack || product.wholesalePrice || 0,
      sellingPricePerKg: product.sellingPricePerKg || product.retailPrice || 0,
      freightPerSack: product.freightPerSack || 0,
      freightPerUnit: product.freightPerUnit || 0,
      handlingPerSack: product.handlingPerSack || 0,
      handlingPerUnit: product.handlingPerUnit || 0,
      srIncentivePerSack: product.srIncentivePerSack || 0,
      srIncentivePerUnit: product.srIncentivePerUnit || 0,
      lowStockThreshold: product.lowStockThreshold,
      // Sunjin-specific deductions
      dealerDiscountPerSack: product.dealerDiscountPerSack || 0,
      cashDiscountPerSack: product.cashDiscountPerSack || 0,
      dealerRebatePerSack: product.dealerRebatePerSack || 0,
      delivery: product.delivery || 0,
      marketingFund: product.marketingFund || 0,
      srMotorPlan: product.srMotorPlan || 0,
      manpowerIncentives: product.manpowerIncentives || 0
    });
    setEditingProduct(product);
    setIsAddModalOpen(true);
  };
  
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.sku?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  
  const getUnitLabel = (unitType, type) => {
    const unit = UNIT_TYPES[unitType];
    return unit ? unit[type] : 'unit';
  };
  
  const isLowStock = (product) => {
    const stock = product.bodegaStock || product.currentStock?.wholesale || 0;
    return stock <= product.lowStockThreshold;
  };
  
  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Products</h1>
          <p className="text-slate-500">Manage your inventory items ({products.length} products)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadProducts} disabled={isLoading}>
            <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
          </Button>
          <Button onClick={() => setIsAddModalOpen(true)} className="bg-green-600 hover:bg-green-700">
            <Plus size={18} className="mr-2" />
            Add Product
          </Button>
        </div>
      </div>
      
      {/* Filters */}
      <Card className="bg-white">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {/* Products List */}
      <div className="grid gap-4">
        {isLoading ? (
          <div className="text-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto"></div>
            <p className="text-slate-500 mt-2">Loading products...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="p-10 text-center">
              <Package className="mx-auto text-slate-300 mb-4" size={48} />
              <p className="text-slate-500 mb-4">
                {products.length === 0 ? 'No products yet. Add your first product!' : 'No products match your search.'}
              </p>
              {products.length === 0 && (
                <Button onClick={() => setIsAddModalOpen(true)} className="bg-green-600 hover:bg-green-700">
                  <Plus size={18} className="mr-2" />
                  Add First Product
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredProducts.map(product => (
            <Card key={product.id} className={`bg-white ${isLowStock(product) ? 'border-red-200' : ''} ${product._pendingSync ? 'border-amber-300' : ''}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  {/* Product Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-800">{product.name}</h3>
                      {isLowStock(product) && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded-full flex items-center gap-1">
                          <AlertTriangle size={12} />
                          Low Stock
                        </span>
                      )}
                      {product._pendingSync && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-600 text-xs rounded-full">
                          Pending Sync
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">{product.category}</span>
                      {product.sku && <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">{product.sku}</span>}
                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-600 rounded">{UNIT_TYPES[product.unitType]?.label || 'Sack → Pack'}</span>
                    </div>
                  </div>
                  
                  {/* Stock Info */}
                  <div className="flex items-center gap-4 text-sm">
                    <div className="text-center">
                      <p className="text-slate-500">Bodega</p>
                      <p className="font-semibold">{product.bodegaStock || product.currentStock?.wholesale || 0} {getUnitLabel(product.unitType, 'wholesale')}s</p>
                    </div>
                    <ArrowRightLeft size={16} className="text-slate-300" />
                    <div className="text-center">
                      <p className="text-slate-500">Shop</p>
                      <p className="font-semibold">{product.shopStock?.wholesale || 0} / {product.shopStock?.retail || 0}</p>
                    </div>
                  </div>
                  
                  {/* Prices - Purchase & Selling with Profit */}
                  <div className="flex items-center gap-3 text-sm">
                    <div className="text-center bg-red-50 px-3 py-2 rounded-lg border border-red-100">
                      <p className="text-xs text-red-500 font-medium">Cost</p>
                      <p className="text-red-700 font-semibold">
                        ₱{(product.purchasePricePerSack || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-red-600">
                        /{UNIT_TYPES[product.unitType]?.wholesale || 'sack'}
                      </p>
                    </div>
                    <div className="text-center bg-green-50 px-3 py-2 rounded-lg border border-green-100">
                      <p className="text-xs text-green-500 font-medium">Sell</p>
                      <p className="text-green-700 font-semibold">
                        ₱{(product.sellingPricePerSack || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-green-600">
                        /{UNIT_TYPES[product.unitType]?.wholesale || 'sack'}
                      </p>
                    </div>
                    {product.purchasePricePerSack > 0 && product.sellingPricePerSack > 0 && (
                      <div className={`text-center px-3 py-2 rounded-lg border ${
                        product.sellingPricePerSack > product.purchasePricePerSack 
                          ? 'bg-emerald-50 border-emerald-200' 
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <p className="text-xs text-slate-500 font-medium">Profit</p>
                        <p className={`font-bold ${
                          product.sellingPricePerSack > product.purchasePricePerSack 
                            ? 'text-emerald-600' 
                            : 'text-red-600'
                        }`}>
                          ₱{(product.sellingPricePerSack - product.purchasePricePerSack).toLocaleString()}
                        </p>
                        <p className={`text-xs ${
                          product.sellingPricePerSack > product.purchasePricePerSack 
                            ? 'text-emerald-600' 
                            : 'text-red-600'
                        }`}>
                          {((product.sellingPricePerSack - product.purchasePricePerSack) / product.purchasePricePerSack * 100).toFixed(0)}%
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEditModal(product)}>
                      <Edit size={16} />
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDeleteProduct(product)}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      
      {/* Add/Edit Product Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Product Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Chicken Grower Feed"
                />
              </div>
              
              <div>
                <Label>Category *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>SKU</Label>
                <Input
                  value={formData.sku}
                  onChange={(e) => setFormData({...formData, sku: e.target.value})}
                  placeholder="e.g., CGF-001"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Supplier Name</Label>
                <Input
                  value={formData.supplierName}
                  onChange={(e) => setFormData({...formData, supplierName: e.target.value})}
                  placeholder="e.g., ABC Distribution"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Unit Conversion Type</Label>
                <Select value={formData.unitType} onValueChange={(v) => setFormData({...formData, unitType: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(UNIT_TYPES).map(([key, value]) => (
                      <SelectItem key={key} value={key}>{value.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {formData.unitType !== 'NO_CONVERSION' && (
                <div className="col-span-2">
                  <Label>Conversion Rate (1 {UNIT_TYPES[formData.unitType]?.wholesale} = X {UNIT_TYPES[formData.unitType]?.retail}s)</Label>
                  <Input
                    type="number"
                    value={formData.conversionRate}
                    onChange={(e) => setFormData({...formData, conversionRate: parseFloat(e.target.value) || 0})}
                    placeholder="e.g., 50"
                  />
                </div>
              )}
              
              {/* ========== PURCHASE PRICES SECTION - PROMINENT ========== */}
              <div className="col-span-2 mt-4 p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border-2 border-red-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">₱</span>
                  </div>
                  <div>
                    <Label className="text-base font-bold text-red-700">Purchase Prices (Your Cost)</Label>
                    <p className="text-xs text-red-600">Enter how much you pay the supplier</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-red-700 font-medium">Cost per {UNIT_TYPES[formData.unitType]?.wholesale || 'Sack'} (₱)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.purchasePricePerSack}
                      onChange={(e) => setFormData({...formData, purchasePricePerSack: parseFloat(e.target.value) || 0})}
                      placeholder="0.00"
                      className="border-red-300 bg-white focus:border-red-500 text-lg font-semibold"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-red-700 font-medium">Cost per {UNIT_TYPES[formData.unitType]?.retail || 'Pack'} (₱)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.purchasePricePerKg}
                      onChange={(e) => setFormData({...formData, purchasePricePerKg: parseFloat(e.target.value) || 0})}
                      placeholder="0.00"
                      className="border-red-300 bg-white focus:border-red-500 text-lg font-semibold"
                    />
                    {formData.purchasePricePerSack > 0 && formData.conversionRate > 0 && (
                      <button 
                        type="button"
                        onClick={() => setFormData({
                          ...formData, 
                          purchasePricePerKg: parseFloat((formData.purchasePricePerSack / formData.conversionRate).toFixed(2))
                        })}
                        className="text-xs text-red-600 hover:text-red-800 mt-1 underline"
                      >
                        Auto-calculate from sack price
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* ========== SELLING PRICES SECTION - PROMINENT ========== */}
              <div className="col-span-2 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">₱</span>
                  </div>
                  <div>
                    <Label className="text-base font-bold text-green-700">Selling Prices (Customer Pays)</Label>
                    <p className="text-xs text-green-600">Enter your selling prices to customers</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-green-700 font-medium">Sell per {UNIT_TYPES[formData.unitType]?.wholesale || 'Sack'} (₱)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.sellingPricePerSack}
                      onChange={(e) => setFormData({...formData, sellingPricePerSack: parseFloat(e.target.value) || 0})}
                      placeholder="0.00"
                      className="border-green-300 bg-white focus:border-green-500 text-lg font-semibold"
                    />
                    {formData.purchasePricePerSack > 0 && formData.sellingPricePerSack > 0 && (
                      <p className={`text-xs mt-1 font-medium ${
                        formData.sellingPricePerSack > formData.purchasePricePerSack ? 'text-green-600' : 'text-red-600'
                      }`}>
                        Margin: ₱{(formData.sellingPricePerSack - formData.purchasePricePerSack).toFixed(2)} 
                        ({((formData.sellingPricePerSack - formData.purchasePricePerSack) / formData.purchasePricePerSack * 100).toFixed(1)}%)
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <Label className="text-green-700 font-medium">Sell per {UNIT_TYPES[formData.unitType]?.retail || 'Pack'} (₱)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.sellingPricePerKg}
                      onChange={(e) => setFormData({...formData, sellingPricePerKg: parseFloat(e.target.value) || 0})}
                      placeholder="0.00"
                      className="border-green-300 bg-white focus:border-green-500 text-lg font-semibold"
                    />
                    {formData.purchasePricePerKg > 0 && formData.sellingPricePerKg > 0 && (
                      <p className={`text-xs mt-1 font-medium ${
                        formData.sellingPricePerKg > formData.purchasePricePerKg ? 'text-green-600' : 'text-red-600'
                      }`}>
                        Margin: ₱{(formData.sellingPricePerKg - formData.purchasePricePerKg).toFixed(2)} 
                        ({((formData.sellingPricePerKg - formData.purchasePricePerKg) / formData.purchasePricePerKg * 100).toFixed(1)}%)
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Freight & Handling Section */}
              <div className="col-span-2 pt-2 border-t">
                <Label className="text-sm font-semibold text-orange-700">Freight & Handling (Deductions)</Label>
              </div>
              
              <div>
                <Label>Freight per {UNIT_TYPES[formData.unitType]?.wholesale || 'Sack'} (₱)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.freightPerSack}
                  onChange={(e) => setFormData({...formData, freightPerSack: parseFloat(e.target.value) || 0})}
                  placeholder="0.00"
                  className="border-orange-200 focus:border-orange-500"
                />
              </div>
              
              <div>
                <Label>Freight per {UNIT_TYPES[formData.unitType]?.retail || 'Pack'} (₱)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.freightPerUnit}
                  onChange={(e) => setFormData({...formData, freightPerUnit: parseFloat(e.target.value) || 0})}
                  placeholder="0.00"
                  className="border-orange-200 focus:border-orange-500"
                />
              </div>
              
              <div>
                <Label>Handling per {UNIT_TYPES[formData.unitType]?.wholesale || 'Sack'} (₱)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.handlingPerSack}
                  onChange={(e) => setFormData({...formData, handlingPerSack: parseFloat(e.target.value) || 0})}
                  placeholder="0.00"
                  className="border-orange-200 focus:border-orange-500"
                />
              </div>
              
              <div>
                <Label>Handling per {UNIT_TYPES[formData.unitType]?.retail || 'Pack'} (₱)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.handlingPerUnit}
                  onChange={(e) => setFormData({...formData, handlingPerUnit: parseFloat(e.target.value) || 0})}
                  placeholder="0.00"
                  className="border-orange-200 focus:border-orange-500"
                />
              </div>
              
              {/* SR Incentives Section */}
              <div className="col-span-2 pt-2 border-t">
                <Label className="text-sm font-semibold text-purple-700">SR Incentives (Deductions)</Label>
              </div>
              
              <div>
                <Label>SR Incentive per {UNIT_TYPES[formData.unitType]?.wholesale || 'Sack'} (₱)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.srIncentivePerSack}
                  onChange={(e) => setFormData({...formData, srIncentivePerSack: parseFloat(e.target.value) || 0})}
                  placeholder="0.00"
                  className="border-purple-200 focus:border-purple-500"
                />
              </div>
              
              <div>
                <Label>SR Incentive per {UNIT_TYPES[formData.unitType]?.retail || 'Pack'} (₱)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.srIncentivePerUnit}
                  onChange={(e) => setFormData({...formData, srIncentivePerUnit: parseFloat(e.target.value) || 0})}
                  placeholder="0.00"
                  className="border-purple-200 focus:border-purple-500"
                />
              </div>
              
              {/* ========== SUNJIN-SPECIFIC DEDUCTIONS ========== */}
              {isSunjinSupplier && (
                <>
                  <div className="col-span-2 mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">SJ</span>
                      </div>
                      <div>
                        <Label className="text-base font-bold text-blue-700">Sunjin Deductions</Label>
                        <p className="text-xs text-blue-600">Additional deductions specific to Sunjin products</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-blue-700 text-sm">Dealer Discount per Sack (₱)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.dealerDiscountPerSack}
                          onChange={(e) => setFormData({...formData, dealerDiscountPerSack: parseFloat(e.target.value) || 0})}
                          placeholder="0.00"
                          className="border-blue-200 focus:border-blue-500"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-blue-700 text-sm">Cash Discount per Sack (₱)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.cashDiscountPerSack}
                          onChange={(e) => setFormData({...formData, cashDiscountPerSack: parseFloat(e.target.value) || 0})}
                          placeholder="0.00"
                          className="border-blue-200 focus:border-blue-500"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-blue-700 text-sm">Dealer Rebate per Sack (₱)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.dealerRebatePerSack}
                          onChange={(e) => setFormData({...formData, dealerRebatePerSack: parseFloat(e.target.value) || 0})}
                          placeholder="0.00"
                          className="border-blue-200 focus:border-blue-500"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-blue-700 text-sm">Delivery (₱)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.delivery}
                          onChange={(e) => setFormData({...formData, delivery: parseFloat(e.target.value) || 0})}
                          placeholder="0.00"
                          className="border-blue-200 focus:border-blue-500"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-blue-700 text-sm">Marketing Fund (₱)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.marketingFund}
                          onChange={(e) => setFormData({...formData, marketingFund: parseFloat(e.target.value) || 0})}
                          placeholder="0.00"
                          className="border-blue-200 focus:border-blue-500"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-blue-700 text-sm">SR Motor Plan (₱)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.srMotorPlan}
                          onChange={(e) => setFormData({...formData, srMotorPlan: parseFloat(e.target.value) || 0})}
                          placeholder="0.00"
                          className="border-blue-200 focus:border-blue-500"
                        />
                      </div>
                      
                      <div className="col-span-2">
                        <Label className="text-blue-700 text-sm">Manpower Incentives (₱)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.manpowerIncentives}
                          onChange={(e) => setFormData({...formData, manpowerIncentives: parseFloat(e.target.value) || 0})}
                          placeholder="0.00"
                          className="border-blue-200 focus:border-blue-500"
                        />
                      </div>
                    </div>
                    
                    {/* Sunjin Deductions Summary */}
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-blue-700 font-medium">Total Sunjin Deductions per Sack:</span>
                        <span className="font-bold text-blue-800">
                          ₱{(
                            (formData.dealerDiscountPerSack || 0) +
                            (formData.cashDiscountPerSack || 0) +
                            (formData.dealerRebatePerSack || 0) +
                            (formData.delivery || 0) +
                            (formData.marketingFund || 0) +
                            (formData.srMotorPlan || 0) +
                            (formData.manpowerIncentives || 0)
                          ).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              {/* Profit Preview with Freight & Handling & SR Incentive deductions */}
              {(formData.purchasePricePerSack > 0 || formData.purchasePricePerKg > 0) && (
                <div className="col-span-2 p-3 bg-gradient-to-r from-slate-50 to-green-50 rounded-lg border">
                  <Label className="text-sm font-semibold text-slate-700">Profit Preview (After All Deductions)</Label>
                  <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                    {formData.purchasePricePerSack > 0 && formData.sellingPricePerSack > 0 && (
                      <div className="space-y-1">
                        <p className="font-medium text-slate-700">Per {UNIT_TYPES[formData.unitType]?.wholesale || 'Sack'}:</p>
                        <p className="text-slate-500">Selling: ₱{formData.sellingPricePerSack.toFixed(2)}</p>
                        <p className="text-slate-500">- Cost: ₱{formData.purchasePricePerSack.toFixed(2)}</p>
                        <p className="text-orange-600">- Freight: ₱{(formData.freightPerSack || 0).toFixed(2)}</p>
                        <p className="text-orange-600">- Handling: ₱{(formData.handlingPerSack || 0).toFixed(2)}</p>
                        <p className="text-purple-600">- SR Incentive: ₱{(formData.srIncentivePerSack || 0).toFixed(2)}</p>
                        {isSunjinSupplier && (
                          <>
                            <p className="text-blue-600">- Dealer Discount: ₱{(formData.dealerDiscountPerSack || 0).toFixed(2)}</p>
                            <p className="text-blue-600">- Cash Discount: ₱{(formData.cashDiscountPerSack || 0).toFixed(2)}</p>
                            <p className="text-blue-600">- Dealer Rebate: ₱{(formData.dealerRebatePerSack || 0).toFixed(2)}</p>
                            <p className="text-blue-600">- Delivery: ₱{(formData.delivery || 0).toFixed(2)}</p>
                            <p className="text-blue-600">- Marketing: ₱{(formData.marketingFund || 0).toFixed(2)}</p>
                            <p className="text-blue-600">- SR Motor: ₱{(formData.srMotorPlan || 0).toFixed(2)}</p>
                            <p className="text-blue-600">- Manpower: ₱{(formData.manpowerIncentives || 0).toFixed(2)}</p>
                          </>
                        )}
                        {(() => {
                          const baseDeductions = (formData.freightPerSack || 0) + (formData.handlingPerSack || 0) + (formData.srIncentivePerSack || 0);
                          const sunjinDeductions = isSunjinSupplier ? (
                            (formData.dealerDiscountPerSack || 0) +
                            (formData.cashDiscountPerSack || 0) +
                            (formData.dealerRebatePerSack || 0) +
                            (formData.delivery || 0) +
                            (formData.marketingFund || 0) +
                            (formData.srMotorPlan || 0) +
                            (formData.manpowerIncentives || 0)
                          ) : 0;
                          const totalDeductions = baseDeductions + sunjinDeductions;
                          const profit = formData.sellingPricePerSack - formData.purchasePricePerSack - totalDeductions;
                          return (
                            <p className={`font-bold pt-1 border-t ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              = Net Profit: ₱{profit.toFixed(2)}
                            </p>
                          );
                        })()}
                      </div>
                    )}
                    {formData.purchasePricePerKg > 0 && formData.sellingPricePerKg > 0 && (
                      <div className="space-y-1">
                        <p className="font-medium text-slate-700">Per {UNIT_TYPES[formData.unitType]?.retail || 'Pack'}:</p>
                        <p className="text-slate-500">Selling: ₱{formData.sellingPricePerKg.toFixed(2)}</p>
                        <p className="text-slate-500">- Cost: ₱{formData.purchasePricePerKg.toFixed(2)}</p>
                        <p className="text-orange-600">- Freight: ₱{(formData.freightPerUnit || 0).toFixed(2)}</p>
                        <p className="text-orange-600">- Handling: ₱{(formData.handlingPerUnit || 0).toFixed(2)}</p>
                        <p className="text-purple-600">- SR Incentive: ₱{(formData.srIncentivePerUnit || 0).toFixed(2)}</p>
                        <p className={`font-bold pt-1 border-t ${
                          (formData.sellingPricePerKg - formData.purchasePricePerKg - (formData.freightPerUnit || 0) - (formData.handlingPerUnit || 0) - (formData.srIncentivePerUnit || 0)) >= 0 
                            ? 'text-green-600' : 'text-red-600'
                        }`}>
                          = Profit: ₱{(formData.sellingPricePerKg - formData.purchasePricePerKg - (formData.freightPerUnit || 0) - (formData.handlingPerUnit || 0) - (formData.srIncentivePerUnit || 0)).toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="col-span-2 pt-2 border-t">
                <Label>Low Stock Threshold</Label>
                <Input
                  type="number"
                  value={formData.lowStockThreshold}
                  onChange={(e) => setFormData({...formData, lowStockThreshold: parseInt(e.target.value) || 0})}
                  placeholder="10"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
            <Button onClick={handleSaveProduct} disabled={isSaving} className="bg-green-600 hover:bg-green-700">
              {isSaving ? 'Saving...' : (editingProduct ? 'Update' : 'Add')} Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NASProducts;
